import React, { useState, useEffect } from 'react';

import { toast, ToastContainer } from 'react-toastify';
import api from '../../services/api';
import Button from '../../components/common/Button/Button';
import styles from './AddProPlanTransaction.module.css';

const AddProPlanTransaction = ({ transactionId, onSubmitted, onCancel }) => {
    const id = transactionId;
    const isEditing = Boolean(id);
    const [loading, setLoading] = useState(false);
    const [fetchingDetail, setFetchingDetail] = useState(isEditing);

    const [formData, setFormData] = useState({
        user_id: '',
        user_name: '',
        transaction_id: '',
        internal_transaction_id: '',
        plan_id: 1,
        plan_type_id: 1,
        plan_activation_type_id: 1,
        plan_activation_date: '',
        plan_deactivation_date: '',
        plan_status: 'Active', // Active, Inactive, Cancelled, Expired
        plan_status_note: ''
    });

    useEffect(() => {
        if (!isEditing) return;

        // The backend `getbyid` SQL function is broken (invoice_id missing), 
        // so we fetch all and filter locally for bulletproof reliability.
        api.get(`/proplantransaction`)
            .then(response => {
                let dataArray = response?.result || response?.data || response || [];
                if (typeof dataArray === 'string') {
                    try { dataArray = JSON.parse(dataArray); } catch (e) { }
                }
                const list = Array.isArray(dataArray) ? dataArray : [];
                const data = list.find(t => String(t.pro_plans_transaction_id) === String(id));

                if (!data) {
                    toast.error("Transaction not found in database");
                    if (onCancel) onCancel();
                    return;
                }

                // Format timestamps for datetime-local input
                const formatDateTime = (isoStr) => {
                    if (!isoStr) return '';
                    const d = new Date(isoStr);
                    if (isNaN(d.getTime())) return '';
                    const pad = (n) => n.toString().padStart(2, '0');
                    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                };

                setFormData({
                    user_id: data.user_id || '',
                    user_name: data.user_name || '',
                    transaction_id: data.transaction_id || '',
                    internal_transaction_id: data.internal_transaction_id || '',
                    plan_id: data.plan_id || 1,
                    plan_type_id: data.plan_type_id || 1,
                    plan_activation_type_id: data.plan_activation_type_id || 1,
                    plan_activation_date: formatDateTime(data.plan_activation_date),
                    plan_deactivation_date: formatDateTime(data.plan_deactivation_date),
                    plan_status: data.plan_status || 'Active',
                    plan_status_note: data.plan_status_note || ''
                });
            })
            .catch(err => {
                console.error("Failed to load transaction details", err);
                toast.error("Failed to load details. They might not exist.");
            })
            .finally(() => {
                setFetchingDetail(false);
            });
    }, [id, isEditing]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic Validation
        if (!formData.user_id || !formData.plan_id || !formData.plan_activation_date) {
            toast.error("User ID, Plan ID, and Activation Date are required.");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...formData,
                user_id: Number(formData.user_id),
                plan_id: Number(formData.plan_id),
                plan_type_id: Number(formData.plan_type_id),
                plan_activation_type_id: Number(formData.plan_activation_type_id),
            };

            if (isEditing) {
                await api.put(`/proplantransaction/${id}`, payload);
                toast.success("✅ Transaction updated successfully!");
            } else {
                await api.post('/proplantransaction', payload);
                toast.success("✅ Transaction created successfully!");
            }

            setTimeout(() => {
                if (onSubmitted) onSubmitted();
            }, 1000);
        } catch (error) {
            console.error("Error saving transaction:", error);
            const errMsg = error.response?.data?.message || "Failed to save transaction";
            toast.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <ToastContainer position="bottom-right" autoClose={2000} />

            <div className={styles.card}>

                {fetchingDetail ? (
                    <div className={styles.loadingState}>Loading transaction details...</div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className={styles.formGrid}>
                            {/* User ID */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>User ID <span className={styles.required}>*</span></label>
                                <input
                                    type="number"
                                    name="user_id"
                                    value={formData.user_id}
                                    onChange={handleChange}
                                    placeholder="e.g. 153"
                                    className={styles.input}
                                    required
                                />
                            </div>

                            {/* User Name */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>User Name</label>
                                <input
                                    type="text"
                                    name="user_name"
                                    value={formData.user_name}
                                    onChange={handleChange}
                                    placeholder="e.g. Aman Gupta"
                                    className={styles.input}
                                />
                            </div>

                            {/* Transaction ID */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Gateway Transaction ID</label>
                                <input
                                    type="text"
                                    name="transaction_id"
                                    value={formData.transaction_id}
                                    onChange={handleChange}
                                    placeholder="e.g. pay_STlc9jq..."
                                    className={styles.input}
                                />
                            </div>

                            {/* Internal Transaction ID */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Internal DB Transaction ID</label>
                                <input
                                    type="text"
                                    name="internal_transaction_id"
                                    value={formData.internal_transaction_id}
                                    onChange={handleChange}
                                    placeholder="e.g. 158"
                                    className={styles.input}
                                />
                            </div>

                            {/* Plan Status */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Status <span className={styles.required}>*</span></label>
                                <select
                                    name="plan_status"
                                    value={formData.plan_status}
                                    onChange={handleChange}
                                    className={styles.input}
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                    <option value="Expired">Expired</option>
                                    <option value="Cancelled">Cancelled</option>
                                </select>
                            </div>

                            {/* Plan IDs Area */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Plan ID <span className={styles.required}>*</span></label>
                                <input
                                    type="number"
                                    name="plan_id"
                                    value={formData.plan_id}
                                    onChange={handleChange}
                                    placeholder="e.g. 1"
                                    className={styles.input}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Plan Type ID <span className={styles.required}>*</span></label>
                                <input
                                    type="number"
                                    name="plan_type_id"
                                    value={formData.plan_type_id}
                                    onChange={handleChange}
                                    placeholder="e.g. 1"
                                    className={styles.input}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Plan Activation Type ID <span className={styles.required}>*</span></label>
                                <input
                                    type="number"
                                    name="plan_activation_type_id"
                                    value={formData.plan_activation_type_id}
                                    onChange={handleChange}
                                    placeholder="e.g. 1"
                                    className={styles.input}
                                    required
                                />
                            </div>



                            {/* Active Timestamp */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Activation Date <span className={styles.required}>*</span></label>
                                <input
                                    type="datetime-local"
                                    name="plan_activation_date"
                                    value={formData.plan_activation_date}
                                    onChange={handleChange}
                                    className={styles.input}
                                    required
                                />
                            </div>

                            {/* Deactive Timestamp */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Deactivation Date</label>
                                <input
                                    type="datetime-local"
                                    name="plan_deactivation_date"
                                    value={formData.plan_deactivation_date}
                                    onChange={handleChange}
                                    className={styles.input}
                                />
                            </div>

                            {/* Plan Status Note */}
                            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                <label className={styles.label}>Status Note (Optional)</label>
                                <textarea
                                    name="plan_status_note"
                                    value={formData.plan_status_note}
                                    onChange={handleChange}
                                    placeholder="Provide internal reasoning or notes..."
                                    className={styles.input}
                                />
                            </div>
                        </div>

                        <div className={styles.actions}>
                            <Button
                                variant="secondary"
                                onClick={onCancel}
                                type="button"
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? "Saving..." : (isEditing ? "Update Transaction" : "Save Transaction")}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AddProPlanTransaction;
